import React, { useState, useLayoutEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import StatusService from '@/services/Status.service';
import Status from '@/types/Status.type';
import { IconMaster, Messages, ColorMaster } from '@/common/constants';
import { useForm, SubmitHandler, Controller, FormProvider } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import Loading from '@/components/common/Loading';
import PageTitle from '@/components/common/PageTitle';
import { CustomTextFieldProp } from '@/components/common/CustomTextField';
import { Box, Paper, Grid, FormControl, FormHelperText, InputLabel, MenuItem, Select, Icon } from '@mui/material';
import HookFormControls from '@/components/common/HookFormControls';
import CommonActionButton from '@/components/common/CommonActionButton';
import URL from '@/common/routes/Url';

import CompanyService from '@/services/Company.service';

const StatusDetail: React.FC = () => {
  const pathParams = useParams<{ id: string }>();
  const [loading, setLoading] = useState(true);
  const [color, setColor] = useState(ColorMaster[0]);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [contPlan, setContPlan] = useState(0);

  const useFormMethods = useForm<Status>({
    defaultValues: {
      name: '',
      sortNo: '',
      returnTimeFlag: false,
      quickUseFlag: false,
      icon: IconMaster[0],
      color: ColorMaster[0],
    },
    shouldUnregister: false,
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    reset,
    setValue,
  } = useFormMethods;

  const validationRules = {
    name: {
      required: 'ステータス名は必須項目です。',
      maxLength: { value: 8, message: 'ステータス名は8字以内で入力してください。' },
    },
    displayName: {
      required: '略名は必須項目です。',
      maxLength: { value: 4, message: '略名は4字以内で入力してください。' },
    },
    sortNo: {
      max: { value: 9999, message: '表示順は9,999字以内で入力してください。' },
      min: { value: 1, message: '表示順は1以上で入力してください。' },
    },
    returnTimeFlag: {},
    quickUseFlag: {},
    icon: { required: 'アイコンは必須項目です。' },
    color: {
      required: 'カラーは必須項目です。',
      pattern: {
        value: new RegExp('^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$'),
        message: 'カラーコードの形式が不正です。',
      },
    },
  };

  const simpleInputs: Array<CustomTextFieldProp> = [
    {
      name: 'name',
      type: 'text',
      label: 'ステータス名*',
      rule: validationRules.name,
    },
    {
      name: 'displayName',
      type: 'text',
      label: '略名*',
      rule: validationRules.displayName,
    },
    {
      name: 'sortNo',
      type: 'number',
      label: '表示順',
      inputProps: { min: 1 },
      rule: validationRules.sortNo,
    },
  ];

  const menuItemIcons = IconMaster.map((iconName, index) => {
    return (
      <MenuItem key={index} value={iconName}>
        <Icon>{iconName}</Icon>
      </MenuItem>
    );
  });

  useLayoutEffect(() => {
    // 会社マスタ取得
    CompanyService.get().then((response: { data: any }) => {
      setContPlan(response.data.contPlan);
    });

    if (!pathParams.id) {
      setLoading(false);
      return;
    }
    
    StatusService.get(pathParams.id)
      .then((response: { data: Status }) => {
        response.data.sortNo = response.data.sortNo ?? '';
        reset(response.data);
        setColor(response.data.color);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        navigate(URL.statuses);
        enqueueSnackbar(Messages.select.failure);
      });
  }, []);

  const onSubmit: SubmitHandler<Status> = async (data: Status) => {
    try {
      if (pathParams.id) {
        await StatusService.update(data, pathParams.id);
        enqueueSnackbar(Messages.update.success, { variant: 'info' });
      } else {
        await StatusService.create(data);
        enqueueSnackbar(Messages.create.success, { variant: 'info' });
      }
      navigate(URL.statuses);
    } catch (error: any) {
      enqueueSnackbar(error.response.data.message);
      return;
    }
  };

  const handleChange = (color: any) => {
    // Stateにカラーコードをセット
    setColor(color.hex);
    // HookFromにカラーコードをセット
    setValue('color', color.hex);
  };

  if (loading) {
    return <Loading />;
  }
  return (
    <Grid container justifyContent="center" alignItems="center">
      <Grid item xs={12} md={8} lg={4}>
        <Box sx= {{ display: { xs: 'block', sm: 'none' } }}>
          <PageTitle>ステータスマスタ ー 編集</PageTitle>
        </Box>
        <Paper variant="outlined" sx={{ my: { xs: 0, md: 0 }, p: { xs: 2, md: 3 } }}>
          <FormProvider {...useFormMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container justifyContent="center" alignItems="center" spacing={3}>
                {simpleInputs.map((input, index) => (
                  <Grid item key={index} xs={12} md={12}>
                    <HookFormControls key={index} {...input} autoComplete="off" />
                  </Grid>
                ))}
                <Grid item xs={12} md={4} lg={4}>
                  <Controller
                    name="icon"
                    control={control}
                    rules={validationRules.icon}
                    render={({ field, fieldState }) => (
                      <FormControl fullWidth error={Boolean(fieldState.error)}>
                        <InputLabel id="icon-label">アイコン</InputLabel>
                        <Select style={{ textAlign: 'center', color: color }} labelId="icon-label" label="アイコン" {...field}>
                          {menuItemIcons}
                        </Select>
                        <FormHelperText>{fieldState.error?.message}</FormHelperText>
                      </FormControl>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={8} lg={8}>
                  <HookFormControls
                    type="color"
                    color={color}
                    onChange={handleChange}
                    rule={validationRules.color}
                    colorMaster={ColorMaster}
                  />
                </Grid>
                <Grid item xs={12}>
                  <HookFormControls
                    type="checkbox"
                    name="returnTimeFlag"
                    label="戻り時間の必須チェック"
                    rule={validationRules.returnTimeFlag}
                  />
                  {contPlan === 1 ?
                    <HookFormControls
                      type="checkbox"
                      name="quickUseFlag"
                      label="簡易ステータス更新で利用"
                      rule={validationRules.quickUseFlag}
                    />
                    : undefined
                  }
                  </Grid>
                <Grid item xs={12} md={12}>
                  <CommonActionButton isSubmitting={isSubmitting} />
                </Grid>
              </Grid>
            </form>
          </FormProvider>
        </Paper>
      </Grid>
    </Grid>
  );
};
export default StatusDetail;
